import { useEffect, useState } from 'react';
import { Collection } from 'models/Collection';
import { useForm } from 'react-hook-form';
import { useSpaceNavigation } from 'xstate/hooks/useSpaceNavigation';
import { CollectionFormSkeleton } from './CollectionFormSkeleton';
import {
  useSaveCollection,
  Variables,
} from 'networking/mindfeed/hooks/useSaveCollection';
import ValidationMessage from 'elements/ValidationMessage';
import { closeModal } from 'xstate/actors/ModalActor';
import { useModal } from 'xstate/hooks/useModal';
import { EmojiPicker } from 'elements/EmojiPicker';
import ClickAwayListener from 'react-click-away-listener';
import classNames from 'classnames';
import { useTranslations } from 'next-intl';
import Button from 'elements/Button';
import CollectionIcon from 'assets/icons/icon-collection.svg';

interface Props {
  collection?: Collection;
  isLoading?: boolean;
  onSuccess?: (collection: Collection) => void;
}

export const CollectionForm: React.FC<Props> = ({
  collection,
  isLoading = false,
  onSuccess,
}) => {
  const t = useTranslations();
  const { isOpen } = useModal();
  const { goToCollection } = useSpaceNavigation();
  // TODO: Refactor this to remove this useless useState and leverage React Hook Form state for `emoji` instead
  const [selectedEmoji, setSelectedEmoji] = useState(collection?.emoji);
  const [isEmojiPickerVisible, setEmojiPickerVisible] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    setFocus,
    reset,
    watch,
    formState: { errors },
  } = useForm({ mode: 'onSubmit' });

  const onCollectionSaved = (collection: Collection) => {
    if (onSuccess) {
      onSuccess(collection);
    }
    reset();
    // Select and navigate to the saved collection so the URL reflects it (the
    // spaces page only auto-fills a box-less landing URL, not this case).
    goToCollection(collection);
    setSelectedEmoji(null);
  };

  const saveCollectionMutation = useSaveCollection({
    onSuccess: onCollectionSaved,
  });

  const onSubmit = async (data) => {
    const collection = {
      ...data,
      ...(selectedEmoji ? { emoji: selectedEmoji } : {}),
    };

    const variables: Variables = {
      collection,
    };

    saveCollectionMutation.mutate(variables);
  };

  const categoryFieldName = 'name';
  const categoryFieldValidation = { required: true, minLength: 2 };
  const canSave = (watch(categoryFieldName)?.trim().length ?? 0) >= 2;

  const onEscapePress = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      setEmojiPickerVisible(false);
    }
  };

  const onEmojiSelection = (emoji: string) => {
    setSelectedEmoji(emoji);
    setEmojiPickerVisible(false);
  };

  const handleEmojiButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
    if (selectedEmoji) {
      setSelectedEmoji(null);
      setEmojiPickerVisible(false);
    } else {
      setEmojiPickerVisible(true);
    }
  };

  const onClickAway = () => {
    setEmojiPickerVisible(false);
  };

  useEffect(() => {
    if (collection) {
      Object.entries(collection).forEach(([key, value]) => {
        setValue(key, value);
      });
      setSelectedEmoji(collection?.emoji || null);
    } else {
      setSelectedEmoji(null);
      setEmojiPickerVisible(false);
    }
  }, [collection]);

  useEffect(() => {
    if (isOpen) {
      setFocus(categoryFieldName);
    }
  }, [isOpen, setFocus]);

  useEffect(() => {
    const handleKeydown = (event) => onEscapePress(event);
    document.addEventListener('keydown', handleKeydown);

    return () => document.removeEventListener('keydown', handleKeydown);
  }, []);

  return isLoading || saveCollectionMutation.isPending ? (
    <CollectionFormSkeleton />
  ) : (
    <form onSubmit={handleSubmit(onSubmit)} className={styles.container}>
      {/* Emoji */}
      <div className={styles.emoji.wrap}>
        <button
          type="button"
          className={styles.emoji.circle}
          onClick={() => setEmojiPickerVisible((visible) => !visible)}
        >
          {selectedEmoji ? (
            <span className={styles.emoji.value}>{selectedEmoji}</span>
          ) : (
            <CollectionIcon className={styles.emoji.placeholder} />
          )}
        </button>

        <button
          type="button"
          className={styles.emoji.toggle}
          onClick={handleEmojiButtonClick}
        >
          {selectedEmoji ? t('common.removeEmoji') : t('common.addEmoji')}
        </button>

        {isEmojiPickerVisible && (
          <ClickAwayListener onClickAway={onClickAway}>
            <div className={styles.emoji.picker}>
              <EmojiPicker
                onEmojiClick={(emojiObject) =>
                  onEmojiSelection(emojiObject.emoji)
                }
              />
            </div>
          </ClickAwayListener>
        )}
      </div>

      {/* Name */}
      <div>
        <label htmlFor="collection-name" className={styles.name.label}>
          {t('common.name')}
        </label>
        <input
          type="text"
          id="collection-name"
          placeholder={t('collections.form.namePlaceholder')}
          autoComplete="off"
          data-1p-ignore
          className={classNames(
            styles.name.input,
            errors[categoryFieldName]
              ? styles.name.inputError
              : styles.name.inputDefault,
          )}
          {...register(categoryFieldName, categoryFieldValidation)}
        />

        {errors?.[categoryFieldName]?.type === 'required' && (
          <ValidationMessage>
            {t('collections.form.nameRequired')}
          </ValidationMessage>
        )}
        {errors?.[categoryFieldName]?.type === 'minLength' && (
          <ValidationMessage>
            {t('forms.tooShort', { min: 2 })}
          </ValidationMessage>
        )}

        <p className={styles.name.helper}>{t('collections.form.helper')}</p>
      </div>

      {/* Footer */}
      <div className={styles.footer}>
        <Button
          type="button"
          variant="solid"
          color="neutral"
          size="sm"
          onClick={closeModal}
        >
          {t('common.cancel')}
        </Button>
        <Button
          variant="solid"
          type="submit"
          color="primary"
          size="sm"
          disabled={!canSave}
          className="bg-mf-blue! hover:bg-mf-blue-600! disabled:opacity-65"
        >
          {t('common.save')}
        </Button>
      </div>
    </form>
  );
};

const styles = {
  container: 'px-6 pb-6 space-y-6',
  emoji: {
    wrap: 'relative flex flex-col items-center gap-2',
    circle: classNames(
      'flex items-center justify-center w-24 h-24 rounded-full',
      'border-2 border-dashed border-gray-300 hover:bg-gray-50 transition-colors',
    ),
    value: 'text-3xl leading-none',
    placeholder: 'w-7 h-7 text-gray-300',
    toggle: 'text-sm text-gray-500 hover:text-gray-700',
    picker: 'absolute z-20 top-full mt-2',
  },
  name: {
    label: 'block mb-2 text-[15px] font-bold text-mf-off-black-950',
    input: classNames(
      'block w-full rounded-xl bg-mf-gray-50 border border-2 px-4 py-2.5',
      'text-base text-mf-off-black-950 placeholder:text-[#A4AEC2] outline-none focus:ring-0',
    ),
    inputDefault: 'border-mf-light-gray-100 focus:border-black',
    inputError: 'border-mf-red',
    helper: 'mt-2 text-sm text-gray-500',
  },
  footer: 'flex justify-end gap-3',
};

export default CollectionForm;
